import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useReplaceExternalUserProjects } from '../../hooks/useExternalUsers';
import { useEscapeHandler } from '../../hooks/useEscapeHandler';
import { ApiClientError } from '../../services/api/client';
import type { ExternalUserRow } from '../../services/api/externalUsers';
import { editProjectsSchema, type EditProjectsInput } from './_schemas';
import { ProjectPicker } from './ProjectPicker';

/**
 * Edit-projects modal for an existing external user.
 *
 * - Pre-fills with the user's current project IDs and disables Submit until the
 *   selection actually changes (RHF `isDirty`).
 * - If the user submits an empty selection, the first click triggers an inline
 *   warning ("User will lose access to all projects. Continue?") + a Confirm
 *   button. The second click submits an empty array.
 * - Mutation key: `useReplaceExternalUserProjects()` is zero-arg; payload is
 *   `{ id, projectIds }`. See 15-01-SUMMARY for the contract.
 *
 * Server-error mapping: same convention as InviteExternalUserModal — keys off
 * `err.message` because `services/api/client.ts` does not preserve `errorCode`.
 */

export interface EditExternalUserProjectsModalProps {
  user: ExternalUserRow | null; // null = closed
  onClose: () => void;
}

function mapEditServerError(err: unknown): string | null {
  if (!(err instanceof ApiClientError)) return null;
  if (err.message === 'INVALID_PROJECT') {
    return 'One or more selected projects are no longer available. Please refresh and try again.';
  }
  return 'Something went wrong, please try again.';
}

export function EditExternalUserProjectsModal({
  user,
  onClose,
}: EditExternalUserProjectsModalProps) {
  const mutation = useReplaceExternalUserProjects();
  const [confirmingEmpty, setConfirmingEmpty] = useState(false);

  const initialIds = user?.projects.map((p) => p.id) ?? [];
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<EditProjectsInput>({
    resolver: zodResolver(editProjectsSchema),
    defaultValues: { projectIds: initialIds },
  });

  // F20-004: extract the unstable mutation method so the effect can list it as
  // a dep without re-running on every render of a fresh mutation object. The
  // previous eslint-disable hid a real hazard: a future TanStack Query change
  // that returns a non-stable mutation could skip mutation.reset() on user
  // switch and leak stale error state into the next row.
  const resetMutation = mutation.reset;
  // When the target user changes (modal opened for a different row), reset the
  // form to that user's current bindings and clear any in-flight confirmation.
  useEffect(() => {
    if (user) {
      reset({ projectIds: user.projects.map((p) => p.id) });
      setConfirmingEmpty(false);
      resetMutation();
    }
  }, [user, reset, resetMutation]);

  const projectIds = watch('projectIds');

  const tryClose = () => {
    if (isDirty && !window.confirm('Discard changes?')) return;
    onClose();
  };

  // Escape key to close (with dirty-confirm gating, mirroring InviteExternalUserModal).
  useEscapeHandler(user !== null, tryClose);

  if (!user) return null;

  const onSubmit = handleSubmit(async (values) => {
    if (values.projectIds.length === 0 && !confirmingEmpty) {
      setConfirmingEmpty(true);
      return;
    }
    try {
      await mutation.mutateAsync({ id: user.id, projectIds: values.projectIds });
      onClose();
    } catch {
      // Surfaced via mutation.error below.
    }
  });

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (isDirty) return; // backdrop ignored when dirty (T-15-03-03)
    onClose();
  };

  const serverError = mutation.isError ? mapEditServerError(mutation.error) : null;
  const isEmpty = projectIds.length === 0;

  return (
    <div
      role="presentation"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        role="dialog"
        aria-labelledby="edit-external-user-projects-title"
        aria-modal="true"
        className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            id="edit-external-user-projects-title"
            className="text-lg font-semibold text-gray-900"
          >
            Edit projects for {user.firstName} {user.lastName}
          </h2>
          <button
            type="button"
            onClick={tryClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {serverError && (
          <div
            role="alert"
            className="mb-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
          >
            {serverError}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Controller
            control={control}
            name="projectIds"
            render={({ field }) => (
              <ProjectPicker
                value={field.value}
                onChange={(next) => {
                  // Any edit invalidates the empty-confirm state.
                  if (confirmingEmpty) setConfirmingEmpty(false);
                  field.onChange(next);
                }}
                disabled={mutation.isPending}
              />
            )}
          />

          {confirmingEmpty && isEmpty && (
            <div
              role="alert"
              className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-900"
            >
              <p>User will lose access to all projects. Continue?</p>
              <div className="mt-2 flex justify-end">
                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="px-3 py-1.5 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  Confirm
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={tryClose}
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isDirty ? 'Discard changes' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={!isDirty || mutation.isPending}
              className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
